import React from "react";

const TEST_OPTIONS = [
    ["cannabinoids", "Cannabinoids"],
    ["pesticides", "Pesticides"],
    ["heavy_metals", "Heavy Metals"],
    ["mycotoxins", "Mycotoxins"],
    ["yeast_and_mold", "Yeast & Mold"],
    ["microbials", "Microbials"],
    ["moisture_content", "Moisture Content"],
    ["water_activity", "Water Activity"],
    ["foreign_matter", "Foreign Matter"],
    ["terpenes", "Terpenes"],
];

const MATRIX_OPTIONS = [ 
    ["F", "Flower"],
    ["E", "Edible"],
    ["P", "Pre-roll"],
    ["C", "Concentrate"],
    ["D", "Drink"],
    ["S", "Synthetic"],
];

const WEIGHT_UNITS = ["g", "oz", "lb", "units"];

const LabOrderSamples = ({ samples, setSamples, createNewSample, availableTests, productMode }) => {
    const handleFieldChange = (sampleId, field, value) => {
        setSamples((prev) =>
            prev.map((s) => (s.id === sampleId ? { ...s, [field]: value } : s))
        );
    };

    const handleTestToggle = (sampleId, testName) => {
        setSamples((prev) =>
            prev.map((s) =>
                s.id === sampleId
                    ? { ...s, tests: { ...s.tests, [testName]: !s.tests[testName] } }
                    : s
            )
        );
    };

    const handleSelectAll = (sampleId, currentTests) => {
        const allSelected = TEST_OPTIONS.every(([key]) => currentTests[key]);
        const newValue = !allSelected;
        setSamples((prev) =>
            prev.map((s) => {
                if (s.id !== sampleId) return s;
                const tests = { ...s.tests };
                TEST_OPTIONS.forEach(([key]) => { tests[key] = newValue; });
                return { ...s, tests };
            })
        );
    };

    const handleRemove = (sampleId) => {
        setSamples((prev) => prev.filter((s) => s.id !== sampleId));
    };

    return (
        <div className="los-container">
            {samples.map((sample, index) => {
                const allSelected = TEST_OPTIONS.every(([key]) => sample.tests[key]);
                const selectedCount = TEST_OPTIONS.filter(([key]) => sample.tests[key]).length;

                return (
                    <div key={sample.id} className="los-card">
                        {/* Card header */}
                        <div className="los-card-header">
                            <div className="los-card-number">Sample {index + 1}</div>
                            {samples.length > 1 && (
                                <button
                                    type="button"
                                    className="los-remove-btn"
                                    onClick={() => handleRemove(sample.id)}
                                    title="Remove sample"
                                >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Two-column fields */}
                        <div className="los-fields-grid">
                            <div className="los-field">
                                <label className="los-label">Sample ID / Cultivar</label>
                                <input
                                    className="los-input"
                                    type="text"
                                    placeholder="e.g. Blue Dream"
                                    value={sample.name}
                                    onChange={(e) => handleFieldChange(sample.id, "name", e.target.value)}
                                />
                            </div>
                            <div className="los-field">
                                <label className="los-label">Batch Number</label>
                                <input
                                    className="los-input"
                                    type="text"
                                    placeholder="e.g. BT-2024-001"
                                    value={sample.batch_number}
                                    onChange={(e) => handleFieldChange(sample.id, "batch_number", e.target.value)}
                                />
                            </div>
                        </div>

                        {productMode === "new" && (
                            <div className="los-field">
                                <label className="los-label">
                                    Batch Weight * <span className="lo-label-hint">(this batch's own quantity, once results are in)</span>
                                </label>
                                <div className="lo-weight-row">
                                    <input
                                        className="los-input lo-input"
                                        type="number"
                                        min="0"
                                        step="any"
                                        required
                                        placeholder="e.g. 28"
                                        value={sample.weight_grams}
                                        onChange={(e) => handleFieldChange(sample.id, "weight_grams", e.target.value)}
                                    />
                                    <select
                                        className="los-input lo-weight-unit"
                                        value={sample.weight_unit}
                                        onChange={(e) => handleFieldChange(sample.id, "weight_unit", e.target.value)}
                                    >
                                        {WEIGHT_UNITS.map((u) => (
                                            <option key={u} value={u}>{u}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        {productMode === "new" && (
                            <div className="los-field">
                                <label className="los-label">
                                    SKU <span className="lo-label-optional">(optional)</span>
                                </label>
                                <input
                                    className="los-input"
                                    type="text"
                                    placeholder="e.g. BLU-001"
                                    value={sample.sku}
                                    onChange={(e) => handleFieldChange(sample.id, "sku", e.target.value)}
                                />
                            </div>
                        )}

                        {/* Matrix row */}
                        <div className="los-field los-field--matrix">
                            <label className="los-label">Matrix</label>
                            <div className="los-matrix-pills">
                                {MATRIX_OPTIONS.map(([val, label]) => (
                                    <button
                                        key={val}
                                        type="button"
                                        className={`los-matrix-pill${sample.matrix === val ? " los-matrix-pill--active" : ""}`}
                                        onClick={() => handleFieldChange(sample.id, "matrix", val)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Tests section */}
                        <div className="los-tests-section">
                            {availableTests !== null && availableTests.length === 0 ? (
                                <p className="los-all-present">All panels already have data for this COA.</p>
                            ) : (
                                <>
                                    <div className="los-tests-header">
                                        <span className="los-tests-label">
                                            Tests
                                            {selectedCount > 0 && (
                                                <span className="los-tests-count">{selectedCount} selected</span>
                                            )}
                                        </span>
                                        {!availableTests && (
                                            <button
                                                type="button"
                                                className={`los-select-all-btn${allSelected ? " los-select-all-btn--active" : ""}`}
                                                onClick={() => handleSelectAll(sample.id, sample.tests)}
                                            >
                                                {allSelected ? "Deselect All" : "Full Panel"}
                                            </button>
                                        )}
                                    </div>
                                    <div className="los-test-pills">
                                        {TEST_OPTIONS
                                            .filter(([key]) => !availableTests || availableTests.includes(key))
                                            .map(([key, label]) => (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    className={`los-test-pill${sample.tests[key] ? " los-test-pill--active" : ""}`}
                                                    onClick={() => handleTestToggle(sample.id, key)}
                                                >
                                                    {sample.tests[key] && (
                                                        <svg className="los-pill-check" width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    )}
                                                    {label}
                                                </button>
                                            ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            })}

            {samples.length < 10 && (
                <button
                    type="button"
                    className="los-add-btn"
                    onClick={() => setSamples((prev) => [...prev, createNewSample()])}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Add Another Sample
                </button>
            )}
        </div>
    );
};

export default LabOrderSamples;
